import { useMemo } from 'react';

import { useGetPredictionTechsQuery } from '@/app/services/equipment';
import {
  useGetYardsQuery,
  useSearchAccountsQuery,
} from '@/app/services/lookups';
import {
  CLINIC_RANCH_HAUL_IN,
  EMBRYO_METHOD_OPTIONS,
  EMBRYO_POSITION,
  EXPORT_ENUMS,
  FRESH_FROZEN,
  IMPLANT_STATUS,
  SEX_EMBRYO,
  STATE_CODE_MAP,
  STATUS_CODE_MAP,
} from '@/constants';
import {
  EMBRYO_STATUS_MAP,
  EMBRYO_STATUS_MAP_WITH_MISSING,
} from '@/pages/fresh-straw/constants';
import { Account } from '@/rtk-query.gql';
import { enumToOptions, mapToOptions } from '@/utils';

import {
  ACCOUNT_TYPE,
  IMPLANT_TYPE,
  PC_RES_OPTIONS,
  THAW_METHOD,
} from './constants';

export const embryoStatusOptions = mapToOptions(EMBRYO_STATUS_MAP, true);
export const embryoStatusOptionsWithMissing = mapToOptions(
  EMBRYO_STATUS_MAP_WITH_MISSING,
  true,
);
export const freshFrozenOptions = enumToOptions(FRESH_FROZEN, true);
export const sexOptions = enumToOptions(SEX_EMBRYO);
export const stateCodeOptions = mapToOptions(STATE_CODE_MAP, true);
export const statusReasonOptions = mapToOptions(STATUS_CODE_MAP, true);
export const exportOptions = enumToOptions(EXPORT_ENUMS);
export const rightLeftOptions = enumToOptions(EMBRYO_POSITION, true);
export const clinicRanchOptions = enumToOptions(CLINIC_RANCH_HAUL_IN, true);
export const implantStatusOptions = enumToOptions(IMPLANT_STATUS, true);
export const implantTypeOptions = enumToOptions(IMPLANT_TYPE, true);
export const thawMethodOptions = enumToOptions(THAW_METHOD, true);
export const methodOptions = EMBRYO_METHOD_OPTIONS;
export const accountTypeOptions = enumToOptions(ACCOUNT_TYPE, true);
export const pcResOptions = enumToOptions(PC_RES_OPTIONS, true);

export const useTechOptions = () => {
  const { data: techsData } = useGetPredictionTechsQuery({
    where: {
      or: [{ farmlocation: { eq: false } }, { farmlocation: { eq: null } }],
    },
  });

  return useMemo(
    () =>
      techsData?.map((tech) => ({ label: tech.name, value: tech.id })) || [],
    [techsData],
  );
};

export const useAccountOptions = () => {
  const { data: accountData } = useSearchAccountsQuery({
    search: '',
    selectionLimit: 100,
  });

  return useMemo(
    () =>
      accountData?.map((acct) => ({
        label: `${acct.name} [${acct.code}]`,
        value: acct.id,
      })) ?? [],
    [accountData],
  );
};

export const useAccountDataMap = (): Map<string, Account> => {
  const { data: accountData } = useSearchAccountsQuery({
    search: '',
    selectionLimit: 100,
  });

  return useMemo(
    () => new Map((accountData ?? []).map((acct) => [acct.id, acct])),
    [accountData],
  );
};

export const useYardOptions = () => {
  const { data: yardData } = useGetYardsQuery({});

  return useMemo(
    () =>
      yardData?.map((yard) => ({
        label: yard.name ?? '',
        value: yard.id,
      })) ?? [],
    [yardData],
  );
};
